/* ==========================================================================
   GWP (Gift With Purchase) - NO APP
   Rule: For every 1 x Testomax in cart, keep 1 x JointFlex Plus in cart (free)

   FIXES:
   1) If theme creates duplicate Testomax lines (due to properties), we MERGE them into one.
   2) If gift becomes duplicate lines, we MERGE it too.
   3) Gift qty always equals TOTAL Testomax qty.

   ========================================================================== */

(() => {
  // =========================
  // CONFIG (YOUR VARIANT IDs)
  // =========================
  const TESTOMAX_VARIANT_IDS = [48120725307597]; // Testomax Pack of 1
  const JOINTFLEX_GIFT_VARIANT_ID = 48121181667533; // JointFlex Plus

  // Prevent loops
  let isSyncing = false;

  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const getCart = async () => {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    if (!res.ok) throw new Error("Failed to fetch cart.js");
    return res.json();
  };

  const addToCart = async (variantId, qty) => {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: variantId, quantity: qty })
    });
    if (!res.ok) throw new Error("Failed to add item");
    return res.json();
  };

  const changeLineItemByKey = async (lineKey, qty) => {
    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: lineKey, quantity: qty })
    });
    if (!res.ok) throw new Error("Failed to change line item");
    return res.json();
  };

  // Pick which line to keep when duplicates exist:
  // Prefer the line which has properties (bundle label), otherwise keep first.
  const pickKeepLine = (lines) => {
    const withProps = lines.find(l => l.properties && Object.keys(l.properties).length > 0);
    return withProps || lines[0];
  };

  // Merge duplicate lines of a given variant (by key operations)
  const mergeLines = async (lines) => {
    if (!lines || lines.length <= 1) return;

    const keep = pickKeepLine(lines);
    const totalQty = lines.reduce((s, l) => s + Number(l.quantity || 0), 0);

    // Set keep line to total
    if (Number(keep.quantity || 0) !== totalQty) {
      await changeLineItemByKey(keep.key, totalQty);
    }

    // Remove all other lines
    for (const l of lines) {
      if (l.key !== keep.key) {
        await changeLineItemByKey(l.key, 0);
      }
    }
  };

  const syncGift = async () => {
    if (isSyncing) return;
    isSyncing = true;

    try {
      // 1) Load cart
      let cart = await getCart();

      // 2) Collect lines
      const testomaxLines = cart.items.filter(i => TESTOMAX_VARIANT_IDS.includes(Number(i.id)));
      const giftLines = cart.items.filter(i => Number(i.id) === JOINTFLEX_GIFT_VARIANT_ID);

      // 3) MERGE duplicates (this fixes your “+ creates new Testomax line” issue visually)
      // If merge happens, cart changes—re-fetch to continue with clean state.
      const hadDupTestomax = testomaxLines.length > 1;
      const hadDupGift = giftLines.length > 1;

      if (hadDupTestomax) {
        await mergeLines(testomaxLines);
      }
      if (hadDupGift) {
        await mergeLines(giftLines);
      }

      if (hadDupTestomax || hadDupGift) {
        cart = await getCart();
      }

      // 4) Recompute totals after merge
      const cleanTestomaxLines = cart.items.filter(i => TESTOMAX_VARIANT_IDS.includes(Number(i.id)));
      const cleanGiftLines = cart.items.filter(i => Number(i.id) === JOINTFLEX_GIFT_VARIANT_ID);

      const testomaxQty = cleanTestomaxLines.reduce((s, i) => s + Number(i.quantity || 0), 0);
      const giftQty = cleanGiftLines.reduce((s, i) => s + Number(i.quantity || 0), 0);
      const giftLineKey = cleanGiftLines[0]?.key || null;

      const desiredGiftQty = testomaxQty; // 1:1

      // 5) Enforce rule
      if (testomaxQty === 0) {
        // No Testomax => remove gift
        if (giftQty > 0 && giftLineKey) {
          await changeLineItemByKey(giftLineKey, 0);
        }
        return;
      }

      if (desiredGiftQty > giftQty) {
        // Add missing gift qty
        await addToCart(JOINTFLEX_GIFT_VARIANT_ID, desiredGiftQty - giftQty);
        return;
      }

      if (desiredGiftQty < giftQty) {
        // Reduce gift qty to match
        if (giftLineKey) {
          await changeLineItemByKey(giftLineKey, desiredGiftQty);
        }
        return;
      }

      // Matched => do nothing
    } catch (e) {
      console.warn("[GWP] Sync error:", e);
    } finally {
      isSyncing = false;
    }
  };

  const syncGiftDebounced = debounce(syncGift, 300);

  // =========================
  // Trigger points
  // =========================
  document.addEventListener("DOMContentLoaded", () => {
    syncGiftDebounced();
  });

  ["cart:updated", "cart:refresh", "cart:change"].forEach((evt) => {
    document.addEventListener(evt, () => syncGiftDebounced());
  });

  // Intercept fetch calls to cart endpoints (AJAX carts)
  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    const result = await originalFetch(...args);

    try {
      const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "";
      if (
        url.includes("/cart/add") ||
        url.includes("/cart/change") ||
        url.includes("/cart/update") ||
        url.includes("/cart/clear")
      ) {
        setTimeout(() => syncGiftDebounced(), 200);
      }
    } catch (_) {}

    return result;
  };

  // Extra: Observe cart DOM changes (cart page + cart drawer)
  const observeCartDom = () => {
    if (!("MutationObserver" in window)) return;

    const targets = [
      document.querySelector("form[action^='/cart']"),
      document.querySelector("#CartDrawer"),
      document.querySelector("[data-cart-drawer]"),
      document.querySelector("[class*='cart']"),
      document.querySelector("#cart"),
      document.body
    ].filter(Boolean);

    const obs = new MutationObserver(() => syncGiftDebounced());
    targets.forEach((t) => obs.observe(t, { childList: true, subtree: true }));
  };

  document.addEventListener("DOMContentLoaded", observeCartDom);

  // Manual hook if needed
  window.__GWP_SYNC_TESTOMAX = syncGiftDebounced;
})();
